import { ApolloError, useMutation, useQuery } from "@apollo/client";
import { useStyletron } from "baseui";
import { Block } from "baseui/block";
import { KIND, SIZE } from "baseui/button";
import { BottomPanel } from "components/bottom-panel";
import { Button } from "components/button";
import { Cell } from "components/cell";
import { Content } from "components/content";
import { Filters } from "components/filters";
import { DataType, FormattedValue } from "components/formatted-value";
import { Grid } from "components/grid";
import { Header } from "components/header";
import { NoPermissionsRedirect } from "components/no-permissions-redirect";
import { PagingControls } from "components/pagination";
import { Table } from "components/table";
import SortingTableHeader, {
  SortDirection,
} from "components/table/sorting-table-header";
import { Tooltip } from "components/tooltip";
import { useAuth } from "contexts/auth-context";
import { useLoading } from "contexts/loading-context";
import { usePaging } from "contexts/paging-context";
import { useProject } from "contexts/project-context";
import { BasicFilter, FiltersState } from "filters";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router";
import { Row } from "react-table";
import { Eye, Plus } from "tabler-icons-react";
import { translateFiltersState } from "utils/filters";
import { PERMISSIONS } from "utils/permissions";
import { renderUserLabel } from "utils/render-user-label";

import { Post } from "../posts";
import { POSTS_FILTERS } from "../posts.filters";
import { PostsField } from "../posts.form";
import { POSTS_INDEX, POSTS_UPDATE } from "../posts.gql";

export default function PostsIndex(): React.ReactElement {
  const { pageSize, currentPage, setTotalCount } = usePaging();
  const [sortBy, setSortBy] = useState<PostsField | null>(PostsField.CreatedAt);
  const [sortDirection, setSortDirection] = useState<SortDirection | null>(
    SortDirection.DESC
  );
  const { enqueueSnackbar } = useSnackbar();
  const [css] = useStyletron();
  const {
    isFetching,
    setIsFetching,
    setIsLoading,
    isPartialFetching,
    setIsPartialFetching,
  } = useLoading();
  const history = useHistory();
  const { checkPermission } = useAuth();
  const [filters, setFilters] = useState<FiltersState>();

  const { activeProject } = useProject();

  const handleSorting = (column: PostsField) => {
    setSortBy(column);
    setSortDirection(
      sortDirection === null
        ? SortDirection.DESC
        : sortDirection === SortDirection.ASC
        ? SortDirection.DESC
        : SortDirection.ASC
    );
  };

  const { refetch, data, loading, error } = useQuery(POSTS_INDEX, {
    variables: {
      pageSize,
      offset: (currentPage - 1) * pageSize,
      sorting: {
        field: sortBy,
        direction: sortDirection,
      },
      filter: {
        and: [
          {
            ...(activeProject && {
              projectId: {
                eq: activeProject?.id,
              },
            }),
          },
          {
            ...(filters && (translateFiltersState(filters) as BasicFilter[])),
          },
        ],
      },
    },
  });

  const posts: { totalCount: number; nodes: Post[] } = data?.posts;

  useEffect(() => {
    if (data?.posts) setTimeout(() => refetch(), 100);
    setIsFetching(true);
  }, []);

  useEffect(() => {
    refetch();
    setIsPartialFetching(true);
  }, [currentPage, pageSize]);

  useEffect(() => {
    refetch();
    setIsPartialFetching(true);
  }, [sortBy, sortDirection]);

  useEffect(() => {
    if (data?.posts) setIsFetching(false);
    if (posts?.totalCount >= 0) setTotalCount(posts?.totalCount);
  }, [data]);

  useEffect(() => {
    if (error?.graphQLErrors)
      enqueueSnackbar({
        message: (error as ApolloError).graphQLErrors.map(
          ({ message }) => message
        )[0],
        variant: "error",
      });
  }, [error]);

  const [updatePost] = useMutation(POSTS_UPDATE);

  const onSubmit = async (post: Post): Promise<void> => {
    setIsLoading(true);

    try {
      await updatePost({
        variables: {
          postUpdateInput: {
            id: post.id,
            isVisible: !post.isVisible,
          },
        },
      });
      enqueueSnackbar({
        message: `Aktualność "${post.name}" jest ${
          post.isVisible ? "niewidoczna" : "widoczna"
        }`,
        variant: "success",
      });
      refetch();
    } catch (error: unknown) {
      enqueueSnackbar({
        message: (error as ApolloError).graphQLErrors.map(
          ({ message }) => message
        )[0],
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const columns = React.useMemo(
    () => [
      {
        Header: (
          <SortingTableHeader
            onClick={() => handleSorting(PostsField.Name)}
            sortDirection={sortBy === PostsField.Name ? sortDirection : null}
          >
            Nazwa
          </SortingTableHeader>
        ),
        accessor: PostsField.Name,
        Cell: ({ row }: { row: Row<Post> }) => (
          <FormattedValue dataType={DataType.Posts} data={row.original.id}>
            {row.original.name}
          </FormattedValue>
        ),
        disableGlobalFilter: true,
      },
      {
        Header: "Projekt",
        accessor: PostsField.Project,
        Cell: ({ row }: { row: Row<Post> }) => (
          <FormattedValue
            dataType={DataType.Projects}
            data={row.original?.project?.id}
            $style={{ fontSize: "12px" }}
          >
            {row.original?.project?.name}
          </FormattedValue>
        ),
      },
      {
        Header: "Wersja językowa",
        accessor: PostsField.Language,
        Cell: ({ row }: { row: Row<Post> }) => (
          <FormattedValue
            dataType={DataType.Language}
            data={row.original?.project?.id}
            $style={{ fontSize: "12px" }}
          >
            {row.original?.language}
          </FormattedValue>
        ),
      },
      {
        Header: (
          <SortingTableHeader
            onClick={() => handleSorting(PostsField.Slug)}
            sortDirection={sortBy === PostsField.Slug ? sortDirection : null}
          >
            Slug
          </SortingTableHeader>
        ),
        accessor: PostsField.Slug,
        Cell: ({ row }: { row: Row<Post> }) => (
          <FormattedValue dataType={DataType.Pre} $style={{ fontSize: "12px" }}>
            {row.original.slug}
          </FormattedValue>
        ),
      },
      {
        Header: (
          <Block display="flex" justifyContent="flex-end">
            <SortingTableHeader
              onClick={() => handleSorting(PostsField.CreatedAt)}
              sortDirection={
                sortBy === PostsField.CreatedAt ? sortDirection : null
              }
            >
              Publikacja
            </SortingTableHeader>
          </Block>
        ),
        accessor: PostsField.PublishedAt,
        Cell: ({ row }: { row: Row<Post> }) => (
          <Block display="flex" justifyContent="flex-end">
            <FormattedValue dataType={DataType.Date}>
              {row.original.publishedAt}
            </FormattedValue>
          </Block>
        ),
      },
      {
        Header: (
          <Block display="flex" justifyContent="flex-end">
            <SortingTableHeader
              onClick={() => handleSorting(PostsField.CreatedAt)}
              sortDirection={
                sortBy === PostsField.CreatedAt ? sortDirection : null
              }
            >
              Utworzenie
            </SortingTableHeader>
          </Block>
        ),
        accessor: PostsField.CreatedAt,
        Cell: ({ row }: { row: Row<Post> }) => (
          <Block display="flex" justifyContent="flex-end">
            <FormattedValue dataType={DataType.Date}>
              {row.original.createdAt}
            </FormattedValue>
          </Block>
        ),
      },
      {
        Header: "Utworzono przez",
        accessor: PostsField.CreatedBy,
        Cell: ({ row }: { row: Row<Post> }) => (
          <FormattedValue
            dataType={DataType.Users}
            data={row.original?.createdBy?.id}
          >
            {row.original?.createdBy
              ? renderUserLabel(row.original?.createdBy)
              : undefined}
          </FormattedValue>
        ),
      },
      {
        Header: (
          <Block display="flex" justifyContent="flex-end">
            <SortingTableHeader
              onClick={() => handleSorting(PostsField.UpdatedAt)}
              sortDirection={
                sortBy === PostsField.UpdatedAt ? sortDirection : null
              }
            >
              Ostatnia aktualizacja
            </SortingTableHeader>
          </Block>
        ),
        accessor: PostsField.UpdatedAt,
        Cell: ({ row }: { row: Row<Post> }) => (
          <Block display="flex" justifyContent="flex-end">
            <FormattedValue dataType={DataType.Date}>
              {row.original.updatedAt}
            </FormattedValue>
          </Block>
        ),
      },
      {
        Header: "Zaktualizowano przez",
        accessor: PostsField.UpdatedBy,
        Cell: ({ row }: { row: Row<Post> }) => (
          <FormattedValue
            dataType={DataType.Users}
            data={row.original?.updatedBy?.id}
          >
            {row.original?.updatedBy
              ? renderUserLabel(row.original?.updatedBy)
              : undefined}
          </FormattedValue>
        ),
      },
      {
        Header: (
          <SortingTableHeader
            onClick={() => handleSorting(PostsField.IsVisible)}
            sortDirection={
              sortBy === PostsField.IsVisible ? sortDirection : null
            }
          >
            Widoczność
          </SortingTableHeader>
        ),
        accessor: PostsField.IsVisible,
        Cell: ({ row }: { row: Row<Post> }) => (
          <Tooltip
            content={`Ustaw jako ${
              row.original.isVisible ? "niewidoczna" : "widoczna"
            }`}
            placement="right"
          >
            <span
              onClick={() => onSubmit(row.original)}
              className={css({
                cursor: "pointer",
              })}
            >
              <FormattedValue dataType={DataType.VisibilityBoolean}>
                {row.original.isVisible}
              </FormattedValue>
            </span>
          </Tooltip>
        ),
      },
      {
        id: "actions",
        Cell: ({ row }: { row: Row<Post> }) => (
          <div
            className={css({
              display: "flex",
              justifyContent: "flex-end",
            })}
          >
            <Button
              kind={KIND.secondary}
              size={SIZE.mini}
              $style={{ marginLeft: "6px" }}
              onClick={() => history.push(`/posts/${row.original.id}`)}
              startEnhancer={<Eye size={14} />}
            />
          </div>
        ),
      },
    ],
    [sortBy, sortDirection]
  );

  if (!checkPermission(PERMISSIONS.post.read)) return <NoPermissionsRedirect />;

  return (
    <article>
      <Header
        title="Aktualności"
        recordsNum={posts?.totalCount}
        labels={["Lista"]}
        buttons={[
          {
            label: "Dodaj aktualność",
            kind: KIND.primary,
            startEnhancer: <Plus size={18} />,
            permission: checkPermission(PERMISSIONS.post.create),
            onClick: () => history.push("/posts/create"),
          },
        ]}
      />
      <Filters
        filters={POSTS_FILTERS.filter((filter: BasicFilter) =>
          activeProject ? filter.id !== PostsField.Project : filter
        )}
        state={filters}
        setState={setFilters}
      />
      <Content filtersOffset>
        <Grid>
          <Cell span={12}>
            <Table<Post>
              columns={columns}
              data={posts?.nodes}
              isLoading={isFetching || isPartialFetching || loading}
              stickLastColumn
            />
          </Cell>
          <Cell span={12}>
            <BottomPanel>
              <PagingControls />
            </BottomPanel>
          </Cell>
        </Grid>
      </Content>
    </article>
  );
}
